import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, Heart, User, Menu, X, Search, LogOut, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';
import CartDrawer from './CartDrawer';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/products' },
    { name: 'About', path: '/about' },
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" data-testid="nav-logo">
            <span className="font-bold text-2xl text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              RENEW<span className="text-[#00F0FF]">|</span>LABS
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Search Bar - Desktop */}
          <form onSubmit={handleSearch} className="hidden md:flex items-center">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search peptides..."
                className="input-field w-64 pl-10 pr-4 py-2 text-sm"
                data-testid="search-input"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A7B5]" />
            </div>
          </form>

          {/* Right Side Icons */}
          <div className="flex items-center space-x-4">
            {/* Wishlist */}
            {user && (
              <Link to="/wishlist" className="p-2 hover:bg-white/5 rounded-lg transition-colors" data-testid="nav-wishlist">
                <Heart className="w-5 h-5 text-[#A1A7B5] hover:text-[#00F0FF]" />
              </Link>
            )}

            {/* Cart Drawer */}
            <Sheet>
              <SheetTrigger asChild>
                <button className="relative p-2 hover:bg-white/5 rounded-lg transition-colors" data-testid="nav-cart">
                  <ShoppingCart className="w-5 h-5 text-[#A1A7B5] hover:text-[#00F0FF]" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#00F0FF] text-black text-xs font-bold rounded-full flex items-center justify-center" data-testid="cart-count">
                      {cartCount}
                    </span>
                  )}
                </button>
              </SheetTrigger>
              <SheetContent className="glass border-l border-[#1E2532] w-full sm:max-w-md">
                <SheetHeader>
                  <SheetTitle className="text-white text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Your Cart
                  </SheetTitle>
                </SheetHeader>
                <CartDrawer />
              </SheetContent>
            </Sheet>

            {/* User Menu */}
            {user ? (
              <div className="relative group">
                <button className="p-2 hover:bg-white/5 rounded-lg transition-colors" data-testid="nav-user-menu">
                  <User className="w-5 h-5 text-[#A1A7B5] hover:text-[#00F0FF]" />
                </button>
                <div className="absolute right-0 mt-2 w-48 glass-card p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="px-3 py-2 border-b border-[#1E2532]">
                    <p className="text-sm text-white font-medium">{user.name}</p>
                    <p className="text-xs text-[#A1A7B5]">{user.email}</p>
                  </div>
                  <Link to="/orders" className="block px-3 py-2 text-sm text-[#A1A7B5] hover:text-white hover:bg-white/5 rounded" data-testid="nav-orders">
                    My Orders
                  </Link>
                  {user.role === 'admin' && (
                    <Link to="/admin" className="flex items-center space-x-2 px-3 py-2 text-sm text-[#00F0FF] hover:text-white hover:bg-white/5 rounded" data-testid="nav-admin">
                      <LayoutDashboard className="w-4 h-4" />
                      <span>Admin Dashboard</span>
                    </Link>
                  )}
                  <button onClick={handleLogout} className="w-full text-left px-3 py-2 text-sm text-[#A1A7B5] hover:text-white hover:bg-white/5 rounded flex items-center space-x-2" data-testid="nav-logout">
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            ) : (
              <Link to="/login" className="p-2 hover:bg-white/5 rounded-lg transition-colors" data-testid="nav-login">
                <User className="w-5 h-5 text-[#A1A7B5] hover:text-[#00F0FF]" />
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 hover:bg-white/5 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5 text-white" /> : <Menu className="w-5 h-5 text-white" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10" data-testid="mobile-menu">
            <form onSubmit={handleSearch} className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search peptides..."
                  className="input-field w-full pl-10 pr-4 py-2 text-sm"
                  data-testid="mobile-search-input"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A7B5]" />
              </div>
            </form>
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-2 nav-link ${location.pathname === link.path ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
            {user?.role === 'admin' && (
              <Link
                to="/admin"
                className="block py-2 text-[#00F0FF] hover:text-white transition-colors"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-admin"
              >
                Admin Dashboard
              </Link>
            )}
            {!user && (
              <Link
                to="/login"
                className="block py-2 text-[#A1A7B5] hover:text-white transition-colors"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-login"
              >
                Log In
              </Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
